import React, { useContext, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Zap, Star, Shield, Loader2, X } from 'lucide-react';
import styles from './styles.module.css';
import { Context } from '../../context/UserContext';
import SubscriptionCheckoutModal from '../../componentes/SubscriptionModal';

const PLANS = [
    {
        id:        'free',
        planName:  null,
        amount:    0,
        name:      'Gratuito',
        price:     'R$ 0',
        period:    '/mês',
        desc:      'Ideal para testar e começar.',
        icon:      <Shield size={22} />,
        color:     'gray',
        benefits:  ['1 nota por semana', '5 produtos no estoque', 'Suporte básico', 'PDF das notas'],
    },
    {
        id:        'plus',
        planName:  'Plano Plus - Mensal',
        amount:    12.90,
        name:      'Plus',
        price:     'R$ 12,90',
        period:    '/mês',
        desc:      'Para freelancers em crescimento.',
        icon:      <Zap size={22} />,
        color:     'indigo',
        highlight: true,
        benefits:  ['5 notas por semana', '25 produtos no estoque', 'Suporte prioritário', 'Backup dos dados', 'Relatórios mensais'],
    },
    {
        id:        'premium',
        planName:  'Plano Premium - Mensal',
        amount:    21.90,
        name:      'Premium',
        price:     'R$ 21,90',
        period:    '/mês',
        desc:      'Para quem não pode ter limites.',
        icon:      <Star size={22} />,
        color:     'gold',
        benefits:  ['Notas ilimitadas', '250 produtos no estoque', 'Suporte 24/7', 'Backup automático', 'Relatórios avançados', 'Acesso antecipado a novidades'],
    },
];

const FAQ_ITEMS = [
    { q: 'Posso cancelar a qualquer momento?',     a: 'Sim. Cancele pelo painel sem fidelidade. O acesso continua até o fim do período pago.' },
    { q: 'O pagamento é seguro?',                  a: 'Sim. Usamos o Mercado Pago com criptografia de ponta a ponta e cobrança automática mensal.' },
    { q: 'O plano gratuito expira?',               a: 'Não. O plano gratuito é permanente, com limite de 1 nota por semana.' },
    { q: 'Como funciona a assinatura recorrente?', a: 'Após assinar, o Mercado Pago cobra automaticamente no mesmo dia do mês. Você pode cancelar a qualquer momento.' },
];

export default function Planos() {
    const { authenticated, user, cancelSubscription, fetchUser } = useContext(Context);
    const navigate = useNavigate();
    const [loadingPlan, setLoadingPlan]   = useState(null);
    const [cancelConfirm, setCancelConfirm] = useState(false);
    const [checkoutPlan, setCheckoutPlan] = useState(null);

    const token = localStorage.getItem('token') ? JSON.parse(localStorage.getItem('token')) : null;

    function handleAssinar(plan) {
        if (plan.id === 'free') {
            if (!authenticated) navigate('/register?plan=free');
            return;
        }

        if (!authenticated) {
            navigate(`/register?plan=${plan.id}`);
            return;
        }

        if (user?.plan === plan.id) return;

        // Abre o checkout com o Card Payment Brick
        setCheckoutPlan(plan);
    }

    async function handleCancelar() {
        if (!user?.currentSubscription) return;
        setLoadingPlan('cancel');
        await cancelSubscription(token, user.currentSubscription);
        await fetchUser();
        setLoadingPlan(null);
        setCancelConfirm(false);
    }

    function getButtonLabel(plan) {
        if (plan.id === 'free') {
            if (!authenticated) return 'Começar grátis';
            if (user?.plan === 'free') return 'Plano atual';
            return 'Fazer downgrade';
        }
        if (!authenticated) return `Assinar ${plan.name}`;
        if (user?.plan === plan.id) return 'Plano atual ✓';
        if (user?.plan !== 'free' && plan.id !== user?.plan) return `Mudar para ${plan.name}`;
        return `Assinar ${plan.name}`;
    }

    function isCurrentPlan(plan) { return authenticated && user?.plan === plan.id; }
    function isDisabled(plan)    { return loadingPlan !== null || isCurrentPlan(plan); }

    return (
        <div className={styles.page}>

            <div className={styles.header}>
                <h1 className={styles.title}>Planos de Assinatura</h1>
                <p className={styles.subtitle}>
                    Cobrança automática mensal via Mercado Pago. Cancele quando quiser.
                </p>
                {authenticated && user?.plan !== 'free' && (
                    <div className={styles.currentPlanBanner}>
                        Você está no plano <strong>{user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}</strong>.
                        {' '}
                        <button className={styles.cancelLink} onClick={() => setCancelConfirm(true)}>
                            Cancelar assinatura
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.cards}>
                {PLANS.map(plan => (
                    <div key={plan.id}
                        className={`${styles.card} ${plan.highlight ? styles.highlight : ''} ${styles[`card_${plan.color}`]} ${isCurrentPlan(plan) ? styles.cardActive : ''}`}
                    >
                        {plan.highlight && !isCurrentPlan(plan) && (
                            <span className={styles.badge}>⚡ Mais popular</span>
                        )}
                        {isCurrentPlan(plan) && (
                            <span className={`${styles.badge} ${styles.badgeActive}`}>✓ Seu plano</span>
                        )}

                        <div className={`${styles.planIcon} ${styles[`icon_${plan.color}`]}`}>
                            {plan.icon}
                        </div>
                        <h2 className={styles.planName}>{plan.name}</h2>
                        <p className={styles.planDesc}>{plan.desc}</p>
                        <div className={styles.priceRow}>
                            <span className={styles.price}>{plan.price}</span>
                            <span className={styles.period}>{plan.period}</span>
                        </div>
                        <ul className={styles.benefits}>
                            {plan.benefits.map((b, i) => (
                                <li key={i}>
                                    <CheckCircle size={15} className={styles.check} /> {b}
                                </li>
                            ))}
                        </ul>

                        <button
                            className={`${styles.btn} ${plan.highlight && !isCurrentPlan(plan) ? styles.btnHighlight : styles.btnDefault} ${isCurrentPlan(plan) ? styles.btnCurrent : ''}`}
                            onClick={() => handleAssinar(plan)}
                            disabled={isDisabled(plan)}
                        >
                            {loadingPlan === plan.id
                                ? <Loader2 size={16} className={styles.spinner} />
                                : getButtonLabel(plan)
                            }
                        </button>

                        {!authenticated && plan.id !== 'free' && (
                            <p className={styles.loginHint}>
                                Já tem conta? <Link to={`/login?plan=${plan.id}`}>Entrar e assinar</Link>
                            </p>
                        )}
                    </div>
                ))}
            </div>

            <div className={styles.mpRow}>
                <p className={styles.mpText}>Pagamento seguro processado por</p>
                <img
                    src="https://imgmp.mlstatic.com/org-img/banners/br/medios/online/468X60.jpg"
                    alt="Mercado Pago"
                    className={styles.mpBadge}
                />
            </div>

            <div className={styles.faqSection}>
                <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
                <div className={styles.faqGrid}>
                    {FAQ_ITEMS.map((item, i) => (
                        <div key={i} className={styles.faqCard}>
                            <h3 className={styles.faqQ}>{item.q}</h3>
                            <p className={styles.faqA}>{item.a}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Modal confirmar cancelamento ── */}
            {cancelConfirm && (
                <div className={styles.cancelOverlay}>
                    <div className={styles.cancelModal}>
                        <button className={styles.cancelClose} onClick={() => setCancelConfirm(false)}>
                            <X size={18} />
                        </button>
                        <h3 className={styles.cancelTitle}>Cancelar assinatura?</h3>
                        <p className={styles.cancelText}>
                            Seu plano voltará para Gratuito ao final do período atual.
                            Esta ação não pode ser desfeita.
                        </p>
                        <div className={styles.cancelActions}>
                            <button className={styles.cancelConfirmBtn} onClick={handleCancelar}
                                disabled={loadingPlan === 'cancel'}>
                                {loadingPlan === 'cancel'
                                    ? <Loader2 size={16} className={styles.spinner} />
                                    : 'Sim, cancelar'
                                }
                            </button>
                            <button className={styles.cancelAbortBtn} onClick={() => setCancelConfirm(false)}>
                                Manter assinatura
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal do Card Payment Brick ── */}
            {checkoutPlan && checkoutPlan.planName && (
                <SubscriptionCheckoutModal
                    plan={checkoutPlan}
                    payerEmail={user?.email}
                    onClose={() => setCheckoutPlan(null)}
                    onSuccess={async () => {
                        setCheckoutPlan(null);
                        await fetchUser();
                    }}
                />
            )}
        </div>
    );
}
